/**
 * Immutable state + patch history for the authoring canvas.
 *
 * Built now even though undo/redo ships in M4 (requirements.md § 1.9). Retrofitting
 * a command model onto a canvas that mutates state in place is a rewrite, whereas
 * establishing it up front costs almost nothing. Every mutation goes through
 * `apply`, which returns a new state and records the transition.
 */

export interface HistoryState<T> {
  present: T;
  past: T[];
  future: T[];
  /** Label of the most recent change, for a future undo tooltip. */
  lastLabel: string | null;
}

const LIMIT = 100;

export function initHistory<T>(present: T): HistoryState<T> {
  return { present, past: [], future: [], lastLabel: null };
}

/**
 * Applies a pure transform and records it.
 *
 * `coalesceKey` merges consecutive changes of the same kind into one history
 * entry — dragging a field produces a mousemove per frame, and undo should step
 * back over the whole drag, not sixty pixels of it.
 */
export function apply<T>(
  h: HistoryState<T>,
  fn: (cur: T) => T,
  label: string,
  coalesceKey?: string
): HistoryState<T> {
  const next = fn(h.present);
  if (next === h.present) return h;

  const merge = coalesceKey !== undefined && coalesceKey === h.lastLabel;
  const past = merge ? h.past : [...h.past, h.present].slice(-LIMIT);

  return { present: next, past, future: [], lastLabel: coalesceKey ?? label };
}

export function undo<T>(h: HistoryState<T>): HistoryState<T> {
  const prev = h.past[h.past.length - 1];
  if (prev === undefined) return h;
  return {
    present: prev,
    past: h.past.slice(0, -1),
    future: [h.present, ...h.future].slice(0, LIMIT),
    lastLabel: null,
  };
}

export function redo<T>(h: HistoryState<T>): HistoryState<T> {
  const next = h.future[0];
  if (next === undefined) return h;
  return {
    present: next,
    past: [...h.past, h.present].slice(-LIMIT),
    future: h.future.slice(1),
    lastLabel: null,
  };
}

export const canUndo = <T>(h: HistoryState<T>) => h.past.length > 0;
export const canRedo = <T>(h: HistoryState<T>) => h.future.length > 0;

/** Ends a coalescing run, so the next change starts a fresh history entry. */
export function commit<T>(h: HistoryState<T>): HistoryState<T> {
  return h.lastLabel === null ? h : { ...h, lastLabel: null };
}
